/* eslint-disable @typescript-eslint/no-non-null-assertion */
import ReactCrop, { centerCrop, makeAspectCrop, Crop, PixelCrop } from 'react-image-crop';
import { canvasPreview } from 'utils/canvasPreview';
import { useDebounceEffect } from 'utils/useDebounceEffect';
import { CloseIcon } from 'components/icons/CloseIcon';

import 'react-image-crop/dist/ReactCrop.css';
import { useContext, useEffect, useRef, useState } from 'react';
import api from 'services/api';
import { MODAL_TYPES } from '../constants';
import { ModalContext } from 'context/ModalContext';

interface IProfileImageUpload {
  imgSrc: any;
  selectedPerson: any;
  peopleData: any[];
  setPeopleData: any;
}

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

export default function ProfileImageUpload({ imgSrc, selectedPerson, peopleData, setPeopleData }: IProfileImageUpload) {
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale] = useState(1);
  const [rotate] = useState(0);
  const [aspect] = useState<number | undefined>(1);
  const { showModal } = useContext(ModalContext);

  useEffect(() => {
    setCrop(undefined);
  }, [imgSrc]);

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    if (aspect) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspect));
    }
  }

  useDebounceEffect(
    async () => {
      if (completedCrop?.width && completedCrop?.height && imgRef.current && previewCanvasRef.current) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, scale, rotate);
      }
    },
    100,
    [completedCrop, scale, rotate]
  );

  const saveProfileImage = () => {
    if (!previewCanvasRef.current) {
      throw new Error('Crop canvas does not exist');
    }

    previewCanvasRef.current.toBlob((blob) => {
      if (!blob) {
        throw new Error('Failed to create blob');
      }
      const formData = new FormData();
      formData.append('photo', blob);
      api
        .put(
          `api/zoom/event/sessions/${selectedPerson.session_id}/speakers/${selectedPerson.hash_id}/upload-photo/`,
          formData
        )
        .then((res) => {
          const newPeopleList = peopleData.map((person: any) => {
            if (person.hash_id === res.data.hash_id) {
              return { ...person, ...res.data };
            }
            return person;
          });
          setPeopleData(newPeopleList);
          showModal(MODAL_TYPES.edit_speaker_profile, {
            selectedPerson: { ...selectedPerson, photo: res.data.photo },
            peopleData: newPeopleList,
            setPeopleData
          });
        });
    });
  };

  const onCancel = () => {
    showModal(MODAL_TYPES.edit_speaker_profile, {
      selectedPerson,
      peopleData,
      setPeopleData
    });
  };

  return (
    <div className="transparent font-lato">
      <div className="relative border bg-gray-bg-dark border-gray-300  bg-white pb-7 pt-9 px-7 w-screen md:w-[520px] md:min-h-[300px] rounded-t-lg">
        <div className="flex mb-5">
          <span className="font-bold text-2xl leading-6 text-[#1A1C1D]">Crop your photo</span>
          <CloseIcon className="ml-auto w-5 h-5 cursor-pointer opacity-70 hover:opacity-100" onClick={onCancel} />
        </div>
        <div className="flex items-center justify-center bg-line">
          {!!imgSrc && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspect}
              className="w-fit h-[460px] rounded-md mx-auto"
              minWidth={72}
              minHeight={72}
            >
              <img
                ref={imgRef}
                alt="Crop me"
                src={imgSrc}
                style={{
                  transform: `scale(${scale}) rotate(${rotate}deg)`,
                  height: 460,
                  width: 'auto',
                  margin: '0 auto',
                  objectFit: 'contain'
                }}
                onLoad={onImageLoad}
              />
            </ReactCrop>
          )}
        </div>
        {!!completedCrop && (
          <>
            <div className="mb-4 mt-6">Preview</div>
            <div>
              <canvas
                ref={previewCanvasRef}
                style={{
                  border: '1px solid black',
                  borderRadius: '4px',
                  objectFit: 'contain',
                  width: '72px',
                  height: '72px'
                }}
              />
            </div>
          </>
        )}
      </div>
      <div className="flex text-[13px] py-4 px-4 bg-white rounded-b-lg border-x">
        <button
          className="py-2 px-3 text-black border border-[#e7eaec] ml-auto rounded-md hover:bg-gray-200"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button className="bg-[#1ab394] py-2 px-4 text-white ml-2 rounded-md" onClick={saveProfileImage}>
          Save Changes
        </button>
      </div>
    </div>
  );
}
